/**
 * Storage Layer - Abstraccion para persistencia de datos
 * Funciona tanto en Reddit (Devvit) como standalone (localStorage)
 */

import { DataManagerWeapon } from '../weapons/DataManagerWeapon';

type StorageProvider = 'devvit' | 'standalone';

interface GameData {
  score: number;
  highScore: number;
  citiesDestroyed: number;
  level: number;
  unlockedLevels: number[]; // Array of unlocked level numbers (1-8)
  biomatter: number; // Currency for shop
  weaponState: WeaponState; // Weapon discovery, ammo, loadout
}

export interface SlotLoadout {
  equipped: string[];   // IDs de armas equipadas en este slot
  activeIndex: number;  // Índice del arma activa
}

export interface WeaponState {
  discovered: string[];              // Armas descubiertas
  ammo: Record<string, number>;      // Munición por arma
  loadout: {
    gun: SlotLoadout;
    tractor: SlotLoadout;
    bomb: SlotLoadout;
  };
}

class GameStorage {
  private provider: StorageProvider;
  private cache: Partial<GameData> = {};
  private devModeSnapshot: { biomatter: number; unlockedLevels: number[]; weaponState: WeaponState } | null = null;
  private devModeEnabled: boolean = false;
  private static readonly DEV_MODE_ENABLED_KEY = 'imbasion_dev_mode_enabled';
  private static readonly DEV_MODE_SNAPSHOT_KEY = 'imbasion_dev_mode_snapshot';
  private static readonly BIOMATTER_MIGRATION_KEY = 'imbasion_biomatter_migrated_v2';

  constructor() {
    this.provider = this.detectProvider();
    this.restoreDevModeState();
    console.log(`[Storage] Provider: ${this.provider}`);
  }

  private restoreDevModeState(): void {
    if (typeof window === 'undefined') return;

    try {
      const wasDevModeEnabled = localStorage.getItem(GameStorage.DEV_MODE_ENABLED_KEY) === '1';
      const snapshotRaw = localStorage.getItem(GameStorage.DEV_MODE_SNAPSHOT_KEY);
      this.devModeSnapshot = snapshotRaw ? JSON.parse(snapshotRaw) as { biomatter: number; unlockedLevels: number[]; weaponState: WeaponState } : null;

      // Safety: never keep dev mode active across page reload.
      // If app closed while dev was ON, restore player snapshot immediately (standalone/localStorage only).
      if (wasDevModeEnabled && this.provider !== 'devvit' && this.devModeSnapshot) {
        localStorage.setItem('imbasion_biomatter', `${this.devModeSnapshot.biomatter}`);
        localStorage.setItem('imbasion_unlocked_levels', JSON.stringify(this.devModeSnapshot.unlockedLevels));
        localStorage.setItem('imbasion_weapon_state', JSON.stringify(this.devModeSnapshot.weaponState));
      }

      this.devModeEnabled = false;
      this.devModeSnapshot = null;
      this.persistDevModeState();
    } catch {
      this.devModeEnabled = false;
      this.devModeSnapshot = null;
      this.persistDevModeState();
    }
  }

  private persistDevModeState(): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(GameStorage.DEV_MODE_ENABLED_KEY, this.devModeEnabled ? '1' : '0');
    if (this.devModeSnapshot) {
      localStorage.setItem(GameStorage.DEV_MODE_SNAPSHOT_KEY, JSON.stringify(this.devModeSnapshot));
    } else {
      localStorage.removeItem(GameStorage.DEV_MODE_SNAPSHOT_KEY);
    }
  }

  private detectProvider(): StorageProvider {
    // Detecta si estamos en Reddit/Devvit
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname.includes('reddit.com') || hostname.includes('redd.it')) {
        return 'devvit';
      }
    }
    return 'standalone';
  }

  isDevvit(): boolean {
    return this.provider === 'devvit';
  }

  // ========== SCORE ==========

  async getScore(): Promise<number> {
    if (this.provider === 'devvit') {
      try {
        const res = await fetch('/api/score');
        if (!res.ok) return 0;
        const data = await res.json();
        return data.score ?? 0;
      } catch {
        return 0;
      }
    }
    return parseInt(localStorage.getItem('imbasion_score') || '0');
  }

  async saveScore(score: number): Promise<void> {
    this.cache.score = score;

    if (this.provider === 'devvit') {
      try {
        await fetch('/api/score', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ score }),
        });
      } catch (e) {
        console.error('[Storage] Failed to save score to Devvit:', e);
      }
    } else {
      localStorage.setItem('imbasion_score', score.toString());
    }

    // Actualizar high score si es necesario
    await this.updateHighScore(score);
  }

  // ========== HIGH SCORE ==========

  async getHighScore(): Promise<number> {
    if (this.provider === 'devvit') {
      try {
        const res = await fetch('/api/highscore');
        if (!res.ok) return 0;
        const data = await res.json();
        return data.highScore ?? 0;
      } catch {
        return 0;
      }
    }
    return parseInt(localStorage.getItem('imbasion_highscore') || '0');
  }

  private async updateHighScore(score: number): Promise<void> {
    const currentHigh = await this.getHighScore();
    if (score > currentHigh) {
      if (this.provider === 'devvit') {
        try {
          await fetch('/api/highscore', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ highScore: score }),
          });
        } catch (e) {
          console.error('[Storage] Failed to save high score to Devvit:', e);
        }
      } else {
        localStorage.setItem('imbasion_highscore', score.toString());
      }
    }
  }

  // ========== GAME PROGRESS ==========

  async getProgress(): Promise<{ citiesDestroyed: number; level: number }> {
    if (this.provider === 'devvit') {
      try {
        const res = await fetch('/api/progress');
        if (!res.ok) return { citiesDestroyed: 0, level: 1 };
        return await res.json();
      } catch {
        return { citiesDestroyed: 0, level: 1 };
      }
    }
    return {
      citiesDestroyed: parseInt(localStorage.getItem('imbasion_cities') || '0'),
      level: parseInt(localStorage.getItem('imbasion_level') || '1'),
    };
  }

  async saveProgress(citiesDestroyed: number, level: number): Promise<void> {
    if (this.provider === 'devvit') {
      try {
        await fetch('/api/progress', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ citiesDestroyed, level }),
        });
      } catch (e) {
        console.error('[Storage] Failed to save progress to Devvit:', e);
      }
    } else {
      localStorage.setItem('imbasion_cities', citiesDestroyed.toString());
      localStorage.setItem('imbasion_level', level.toString());
    }
  }

  // ========== UNLOCKED LEVELS ==========

  async getUnlockedLevels(): Promise<number[]> {
    if (this.provider === 'devvit') {
      try {
        const res = await fetch('/api/unlockedLevels');
        if (!res.ok) return [1]; // Level 1 always unlocked
        const data = await res.json();
        return data.unlockedLevels ?? [1];
      } catch {
        return [1];
      }
    }
    const stored = localStorage.getItem('imbasion_unlocked_levels');
    if (!stored) return [1]; // Level 1 always unlocked
    try {
      return JSON.parse(stored);
    } catch {
      return [1];
    }
  }

  async unlockLevel(level: number): Promise<void> {
    const current = await this.getUnlockedLevels();
    if (current.includes(level)) return; // Already unlocked

    const updated = [...current, level].sort((a, b) => a - b);

    if (this.provider === 'devvit') {
      try {
        await fetch('/api/unlockedLevels', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ unlockedLevels: updated }),
        });
      } catch (e) {
        console.error('[Storage] Failed to save unlocked levels to Devvit:', e);
      }
    } else {
      localStorage.setItem('imbasion_unlocked_levels', JSON.stringify(updated));
    }
  }

  async isLevelUnlocked(level: number): Promise<boolean> {
    const unlocked = await this.getUnlockedLevels();
    return unlocked.includes(level);
  }

  // ========== BIOMATTER (Currency) ==========

  async getBiomatter(): Promise<number> {
    if (this.provider === 'devvit') {
      try {
        const res = await fetch('/api/biomatter');
        if (!res.ok) return 0;
        const data = await res.json();
        return data.biomatter ?? 0;
      } catch {
        return 0;
      }
    }
    let raw = localStorage.getItem('imbasion_biomatter');
    const migrated = localStorage.getItem(GameStorage.BIOMATTER_MIGRATION_KEY) === '1';

    if (!migrated) {
      // Legacy fallback used to be 40000; migrate once to 0 for clean player mode.
      if (raw === null || raw === '40000') {
        localStorage.setItem('imbasion_biomatter', '0');
        localStorage.setItem(GameStorage.BIOMATTER_MIGRATION_KEY, '1');
        raw = '0';
        return 0;
      }
      localStorage.setItem(GameStorage.BIOMATTER_MIGRATION_KEY, '1');
      raw = localStorage.getItem('imbasion_biomatter');
    }

    return parseInt(raw || '0');
  }

  async saveBiomatter(amount: number): Promise<void> {
    if (this.provider === 'devvit') {
      try {
        await fetch('/api/biomatter', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ biomatter: amount }),
        });
      } catch (e) {
        console.error('[Storage] Failed to save biomatter to Devvit:', e);
      }
    } else {
      localStorage.setItem('imbasion_biomatter', amount.toString());
    }
  }

  async addBiomatter(amount: number): Promise<number> {
    const current = await this.getBiomatter();
    const newAmount = current + amount;
    await this.saveBiomatter(newAmount);
    return newAmount;
  }

  // ========== WEAPON STATE ==========

  private getDefaultWeaponState(testingMode: boolean = true): WeaponState {
    // Try to get defaults from DataManagerWeapon
    if (DataManagerWeapon.isLoaded()) {
      const defaults = DataManagerWeapon.getDefaultWeaponState(testingMode);
      if (defaults) {
        return defaults;
      }
    }

    // Fallback if DataManagerWeapon not loaded yet (loud fail)
    console.error('[Storage] DataManagerWeapon defaults not loaded, using minimal fallback');
    return {
      discovered: ['lasergun', 'punytractorbeam', 'plasmabomb'],
      ammo: { plasmabomb: 10 },
      loadout: {
        gun: { equipped: ['lasergun'], activeIndex: 0 },
        tractor: { equipped: ['punytractorbeam'], activeIndex: 0 },
        bomb: { equipped: ['plasmabomb'], activeIndex: 0 },
      },
    };
  }

  async getWeaponState(): Promise<WeaponState> {
    if (this.provider === 'devvit') {
      try {
        const res = await fetch('/api/weaponState');
        if (!res.ok) return this.getDefaultWeaponState();
        const data = await res.json();
        return data.weaponState ?? this.getDefaultWeaponState();
      } catch {
        return this.getDefaultWeaponState();
      }
    }
    const stored = localStorage.getItem('imbasion_weapon_state');
    if (!stored) return this.getDefaultWeaponState();
    try {
      return JSON.parse(stored);
    } catch {
      return this.getDefaultWeaponState();
    }
  }

  async saveWeaponState(state: WeaponState): Promise<void> {
    if (this.provider === 'devvit') {
      try {
        await fetch('/api/weaponState', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ weaponState: state }),
        });
      } catch (e) {
        console.error('[Storage] Failed to save weapon state to Devvit:', e);
      }
    } else {
      localStorage.setItem('imbasion_weapon_state', JSON.stringify(state));
    }
  }

  async discoverWeapon(weaponId: string, initialAmmo?: number): Promise<WeaponState> {
    const state = await this.getWeaponState();
    if (!state.discovered.includes(weaponId)) {
      state.discovered.push(weaponId);
      if (initialAmmo !== undefined && initialAmmo > 0) {
        state.ammo[weaponId] = (state.ammo[weaponId] ?? 0) + initialAmmo;
      }
      await this.saveWeaponState(state);
    }
    return state;
  }

  async setSlotLoadout(slot: 'gun' | 'tractor' | 'bomb', equipped: string[], activeIndex: number = 0): Promise<WeaponState> {
    const state = await this.getWeaponState();
    state.loadout[slot] = { equipped, activeIndex };
    await this.saveWeaponState(state);
    return state;
  }

  async setActiveWeapon(slot: 'gun' | 'tractor' | 'bomb', activeIndex: number): Promise<WeaponState> {
    const state = await this.getWeaponState();
    state.loadout[slot].activeIndex = activeIndex;
    await this.saveWeaponState(state);
    return state;
  }

  async addAmmo(weaponId: string, amount: number): Promise<number> {
    const state = await this.getWeaponState();
    state.ammo[weaponId] = (state.ammo[weaponId] ?? 0) + amount;
    await this.saveWeaponState(state);
    return state.ammo[weaponId];
  }

  async getAmmo(weaponId: string): Promise<number> {
    const state = await this.getWeaponState();
    return state.ammo[weaponId] ?? 0;
  }

  async useAmmo(weaponId: string, amount: number = 1): Promise<boolean> {
    const state = await this.getWeaponState();
    const current = state.ammo[weaponId] ?? 0;
    if (current < amount) return false;
    state.ammo[weaponId] = current - amount;
    await this.saveWeaponState(state);
    return true;
  }

  // ========== RESET ==========

  async resetAll(): Promise<void> {
    this.cache = {};

    if (this.provider === 'devvit') {
      try {
        await fetch('/api/reset', { method: 'POST' });
      } catch (e) {
        console.error('[Storage] Failed to reset Devvit data:', e);
      }
    } else {
      localStorage.removeItem('imbasion_score');
      localStorage.removeItem('imbasion_highscore');
      localStorage.removeItem('imbasion_cities');
      localStorage.removeItem('imbasion_level');
      localStorage.removeItem('imbasion_unlocked_levels');
      localStorage.removeItem('imbasion_biomatter');
      localStorage.removeItem('imbasion_weapon_state');
    }
  }

  // Reset solo weapon state
  // testingMode=true gives all weapons for testing, false gives production defaults
  async resetWeaponState(testingMode: boolean = true): Promise<WeaponState> {
    if (this.provider !== 'devvit') {
      localStorage.removeItem('imbasion_weapon_state');
    }
    const defaultState = this.getDefaultWeaponState(testingMode);
    await this.saveWeaponState(defaultState);
    console.log(`[Storage] Weapon state reset to ${testingMode ? 'testing' : 'production'} defaults`);
    return defaultState;
  }

  async isDevModeEnabled(): Promise<boolean> {
    return this.devModeEnabled;
  }

  async enableDevMode(config?: { biomatter?: number; maxLevel?: number }): Promise<void> {
    if (this.devModeEnabled) return;

    const biomatter = config?.biomatter ?? 999999;
    const maxLevel = Math.max(1, config?.maxLevel ?? 1);
    const unlockedLevels = Array.from({ length: maxLevel }, (_, i) => i + 1);

    this.devModeSnapshot = {
      biomatter: await this.getBiomatter(),
      unlockedLevels: await this.getUnlockedLevels(),
      weaponState: await this.getWeaponState(),
    };

    await this.saveBiomatter(biomatter);
    if (this.provider === 'devvit') {
      try {
        await fetch('/api/unlockedLevels', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ unlockedLevels }),
        });
      } catch (e) {
        console.error('[Storage] Failed to save unlocked levels to Devvit:', e);
      }
    } else {
      localStorage.setItem('imbasion_unlocked_levels', JSON.stringify(unlockedLevels));
    }
    await this.resetWeaponState(true);

    this.devModeEnabled = true;
    this.persistDevModeState();
  }

  async disableDevMode(): Promise<void> {
    if (!this.devModeEnabled) return;
    if (!this.devModeSnapshot) {
      this.devModeEnabled = false;
      this.persistDevModeState();
      return;
    }

    await this.saveBiomatter(this.devModeSnapshot.biomatter);
    if (this.provider === 'devvit') {
      try {
        await fetch('/api/unlockedLevels', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ unlockedLevels: this.devModeSnapshot.unlockedLevels }),
        });
      } catch (e) {
        console.error('[Storage] Failed to restore unlocked levels in Devvit:', e);
      }
    } else {
      localStorage.setItem('imbasion_unlocked_levels', JSON.stringify(this.devModeSnapshot.unlockedLevels));
    }
    await this.saveWeaponState(this.devModeSnapshot.weaponState);

    this.devModeSnapshot = null;
    this.devModeEnabled = false;
    this.persistDevModeState();
  }

  async toggleDevMode(config?: { biomatter?: number; maxLevel?: number }): Promise<boolean> {
    if (this.devModeEnabled) {
      await this.disableDevMode();
      return false;
    }
    await this.enableDevMode(config);
    return true;
  }
}

// Singleton - una instancia global
export const storage = new GameStorage();
